import { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import type { Moment } from "moment";
import { Form, FormInstance } from "antd";
import type { IOfferListSearch } from "../../../store/slices/offering/types";
import { LoadingStatusCode, Offer } from "../../../lib/types";
import { toHttpFormat } from "../../../lib/utils";
import { fetchOfferListRoute } from "../../../lib/routes/offering";
import { catchApiError } from "../../../lib/utils/catchApiError";
import {
  IOfferListSelectMode,
  IOfferListViewMode,
} from "../../../containers/OfferContainer/context/types";

// Возвращаемые значения:\
export interface IOfferListProps {
  form: FormInstance<any>;
  isCatalogOpen: boolean;
  setIsCatalogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  catalogExpandedKeys: string[];
  setCatalogExpandedKeys: React.Dispatch<React.SetStateAction<string[]>>;
  treeExpandedKeys: string[];
  setTreeExpandedKeys: React.Dispatch<React.SetStateAction<string[]>>;
  searchListValues: IOfferListSearch;
  onChangeSearchValue: (field: Partial<IOfferListSearch<Moment>>) => void;
  isSearchOpen: boolean;
  setIsSearchOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedOffer: Offer | undefined;
  setSelectedOffer: React.Dispatch<React.SetStateAction<Offer | undefined>>;
  onResetSearch: () => void;
  onSearch: (paginationProps: { page: number; size?: number }) => Promise<void>;
  toggleCatalogOpen: () => void;
  toggleSearchOpen: () => void;
  offerListSelectMode: IOfferListSelectMode;
  setOfferListSelectMode: React.Dispatch<React.SetStateAction<IOfferListSelectMode>>;
  offerListViewMode: IOfferListViewMode;
  setOfferListViewMode: React.Dispatch<React.SetStateAction<IOfferListViewMode>>;
  offerList: Offer[];
  offerListLoading: LoadingStatusCode;
  offerListTotalElements: number;
}

interface IProps {
  initialSearchValues?: IOfferListSearch;
  onReset?: () => void;
  initialSelectMode?: IOfferListSelectMode; // Поведение по нажатии на строку (выбор, переход в детали и пр.)
  initialOfferListViewMode: IOfferListViewMode;
}

export const useOfferList = (props: IProps): IOfferListProps => {
  const {
    initialSearchValues = {
      onDate: moment(),
      offeringStatusId: 2,
      page: 0,
      size: 10,
    },
    onReset,
    initialSelectMode = "select",
    initialOfferListViewMode,
  } = props;
  initialSearchValues.onDate = initialSearchValues.onDate.startOf("day");
  const [isCatalogOpen, setIsCatalogOpen] = useState(true);
  const [catalogExpandedKeys, setCatalogExpandedKeys] = useState<string[]>([]);
  const [treeExpandedKeys, setTreeExpandedKeys] = useState<string[]>([]);
  const [searchListValues, setSearchListValues] = useState<IOfferListSearch>(initialSearchValues);
  const [isSearchOpen, setIsSearchOpen] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState<Offer | undefined>();
  const [offerListData, setOfferListData] = useState<{
    offerListLoading: LoadingStatusCode;
    offerListTotalElements: number;
  }>({
    offerListLoading: "idle",
    offerListTotalElements: 0,
  });
  const [offerList, setOfferList] = useState<Offer[]>([]);
  const [offerListSelectMode, setOfferListSelectMode] = useState<IOfferListSelectMode>(
    initialSelectMode
  );

  const [offerListViewMode, setOfferListViewMode] = useState<IOfferListViewMode>(
    initialOfferListViewMode
  );

  const toggleCatalogOpen = () => {
    setIsCatalogOpen((prev) => !prev);
  };

  const toggleSearchOpen = () => {
    setIsSearchOpen((prev) => !prev);
  };

  const onChangeSearchValue = (field: Partial<IOfferListSearch<Moment>>) => {
    const fieldName = Object.keys(field)[0];
    setSearchListValues((prev) => {
      const values: IOfferListSearch = { ...prev, ...field };
      return values;
    });
  };

  const getSearchValues = (paginationProps: {
    page: number;
    size?: number;
  }): IOfferListSearch<string> => {
    const onDate = searchListValues.onDate ? toHttpFormat(searchListValues.onDate) : "";
    const includeFutureDates = searchListValues.onDate?.isSameOrAfter(moment());
    const searchValues: IOfferListSearch<string> = {
      ...searchListValues,
      onDate,
      ...paginationProps,
      includeFutureDates,
      includePastDates: false,
    };
    if (!searchListValues.agreementId) {
      searchValues.offeringTypeId = 1;
    }
    return searchValues;
  };

  const onSearch = async (paginationProps: { page: number; size?: number }) => {
    setSelectedOffer(undefined);
    setOfferListData((prev) => ({
      offerListLoading: "pending",
      offerListTotalElements: 0,
    }));
    const searchValues = getSearchValues(paginationProps);
    try {
      const response = axios.get(fetchOfferListRoute(searchValues));
      const { data } = await response;
      if (paginationProps.page === 0) {
        // Первичный поиск
        setOfferList(data.content);
      } else {
        // Для догрузки по скроллу: пополняем список новыми
        setOfferList((prev) => [...prev, ...data.content]);
      }
      setOfferListData({
        offerListLoading: "fulfilled",
        offerListTotalElements: data.totalElements,
      });
    } catch (e) {
      setOfferList([]);
      setOfferListData({
        offerListLoading: "rejected",
        offerListTotalElements: 0,
      });
      catchApiError({
        e,
        title: "Ошибка загрузки списка предложений. Попробуйте позже",
      });
    }
  };

  const [form] = Form.useForm();
  useEffect(() => {
    form.setFieldsValue(initialSearchValues);
  }, []);

  const onResetSearch = () => {
    onReset && onReset();
    setSearchListValues(initialSearchValues);
    setSelectedOffer(undefined);
    setOfferList([]);
    setOfferListData({
      // offerList: [],
      offerListLoading: "idle",
      offerListTotalElements: 0,
    });
    form.resetFields();
    setTimeout(() => {
      form.setFieldsValue(initialSearchValues);
    }, 100);
  };

  return {
    form,
    isCatalogOpen,
    setIsCatalogOpen,
    catalogExpandedKeys,
    setCatalogExpandedKeys,
    treeExpandedKeys,
    setTreeExpandedKeys,
    searchListValues,
    onChangeSearchValue,
    isSearchOpen,
    setIsSearchOpen,
    selectedOffer,
    setSelectedOffer,
    onResetSearch,
    onSearch,
    toggleCatalogOpen,
    toggleSearchOpen,
    offerListSelectMode,
    setOfferListSelectMode,
    offerListViewMode,
    setOfferListViewMode,
    ...offerListData,
    offerList,
  };
};
